import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { slugifySkillDisplayName } from "encamp-shared/src/skills/slug";
import { SkillAudience, SkillScope } from "generated-graphql/graphql";
import { useCallback, useState } from "react";

import type { SkillEditorFormValues } from "./SkillEditor";
import type { SkillRow } from "./skillsGridColumns";

import { extraDraftFiles, skillMdForCreate } from "./draftSkillFiles";
import {
  CREATE_SKILL_MUTATION,
  DELETE_SKILL_MUTATION,
  SKILLS_QUERY,
  UPDATE_SKILL_MUTATION,
  WRITE_SKILL_FILE_MUTATION,
} from "./skillQueries";

type UseManageSkillsMutationsArgs = {
  tenantId: string | undefined;
  isStaff: boolean;
  onDeleted: (skillId: string) => void;
  onSaved: (skill?: SkillRow) => void;
};

function scopeToSave(scope: SkillScope, isStaff: boolean): SkillScope {
  if (scope === SkillScope.Global && isStaff) {
    return SkillScope.Global;
  }
  return SkillScope.Tenant;
}

function audienceToSave(audience: SkillAudience, isStaff: boolean): SkillAudience {
  if (!isStaff && audience === SkillAudience.Staff) {
    return SkillAudience.Private;
  }
  return audience;
}

type WriteSkillFile = (options: {
  variables: { input: { id: string; tenantId: string; path: string; content: string } };
}) => Promise<unknown>;

async function uploadExtraDraftFiles(
  writeSkillFile: WriteSkillFile,
  skillId: string,
  tenantId: string,
  draftFiles: Record<string, string>
): Promise<void> {
  for (const [path, content] of extraDraftFiles(draftFiles)) {
    await writeSkillFile({
      variables: { input: { id: skillId, tenantId, path, content } },
    });
  }
}

function useDeleteManagedSkill(params: {
  tenantId: string | undefined;
  onDeleted: (skillId: string) => void;
}) {
  const { tenantId, onDeleted } = params;
  const alerts = useAlerts();
  const [deleteSkillMutation, { loading: deleting }] = useMutation(DELETE_SKILL_MUTATION, {
    refetchQueries: [SKILLS_QUERY],
  });

  const deleteSkill = useCallback(
    async (skill: SkillRow) => {
      if (!tenantId) {
        return;
      }

      try {
        await deleteSkillMutation({
          variables: { input: { id: skill.id, tenantId } },
        });
        alerts.success("Skill deleted");
        onDeleted(skill.id);
      } catch (error) {
        alerts.error(error instanceof Error ? error.message : "Failed to delete skill");
      }
    },
    [alerts, deleteSkillMutation, onDeleted, tenantId]
  );

  return { deleteSkill, deleting };
}

export function useManageSkillsMutations({
  tenantId,
  isStaff,
  onDeleted,
  onSaved,
}: UseManageSkillsMutationsArgs) {
  const alerts = useAlerts();
  const [persisting, setPersisting] = useState(false);

  const [createSkill, { loading: creating }] = useMutation(CREATE_SKILL_MUTATION, {
    refetchQueries: [SKILLS_QUERY],
  });
  const [writeSkillFile] = useMutation(WRITE_SKILL_FILE_MUTATION);
  const [updateSkill, { loading: updating }] = useMutation(UPDATE_SKILL_MUTATION, {
    refetchQueries: [SKILLS_QUERY],
  });
  const { deleteSkill, deleting } = useDeleteManagedSkill({ tenantId, onDeleted });

  const saveSkill = useCallback(
    async (
      mode: "create" | "edit",
      values: SkillEditorFormValues,
      editingSkill: SkillRow | null,
      draftFiles: Record<string, string>
    ) => {
      if (!tenantId) {
        return;
      }

      setPersisting(true);
      try {
        if (mode === "create") {
          const scope = scopeToSave(values.scope, isStaff);
          const result = await createSkill({
            variables: {
              input: {
                ...(scope === SkillScope.Global ? {} : { tenantId }),
                scope,
                audience: audienceToSave(values.audience, isStaff),
                slug: slugifySkillDisplayName(values.displayName),
                displayName: values.displayName.trim(),
                description: values.description.trim(),
                initialSkillMd: skillMdForCreate(draftFiles),
              },
            },
          });
          const created = result.data?.createSkill;
          if (!created) {
            alerts.error("Failed to save skill");
            return;
          }
          try {
            await uploadExtraDraftFiles(writeSkillFile, created.id, tenantId, draftFiles);
          } catch (error) {
            alerts.error(
              error instanceof Error
                ? error.message
                : "Skill created, but some files failed to save"
            );
            onSaved(created);
            return;
          }
          alerts.success("Skill created");
          onSaved(created);
        } else if (editingSkill) {
          const scope = scopeToSave(values.scope, isStaff);
          await updateSkill({
            variables: {
              input: {
                id: editingSkill.id,
                tenantId,
                scope,
                audience: audienceToSave(values.audience, isStaff),
                enabled: values.enabled,
                slug: slugifySkillDisplayName(values.displayName),
                displayName: values.displayName.trim(),
                description: values.description.trim(),
              },
            },
          });
          alerts.success("Skill updated");
          onSaved();
        }
      } catch (error) {
        alerts.error(error instanceof Error ? error.message : "Failed to save skill");
      } finally {
        setPersisting(false);
      }
    },
    [alerts, createSkill, isStaff, onSaved, tenantId, updateSkill, writeSkillFile]
  );

  return {
    saveSkill,
    deleteSkill,
    saving: creating || updating || persisting,
    deleting,
  };
}
